import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, FileText, Search, Upload } from "lucide-react";
import { AdminPageShell } from "@/components/shared/admin-page-shell";
import { EmptyState } from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ContentPublishBadge,
  ContentReviewBadge,
  ContentTypeBadge,
} from "@/features/content/components/ContentPageStatus";
import { REVIEW_STATUS_LABEL } from "@/features/content/lib/content-review-status";
import { ContentPageActions } from "@/features/content/components/ContentPageActions";
import { CreatePageDialog } from "@/features/content/components/CreatePageDialog";
import { ExportFormatMenu } from "@/features/content/components/ExportFormatMenu";
import { contentService } from "@/features/content/services/contentService";
import type { ContentListParams } from "@/features/content/services/contentService";
import { useContentPermissions } from "@/features/content/hooks/useContentPermissions";
import {
  useAssignDepartmentBulk,
  usePublishBulk,
} from "@/features/content/hooks/useContentMutations";
import { useDepartments } from "@/features/departments/hooks/useDepartmentsQueries";
import { useAllowedDepartments } from "@/hooks/useDepartmentScope";
import { userService } from "@/features/users/services/userService";
import { handleApiError } from "@/lib/error-handler";
import { formatOptionalArabicDateTime } from "@/lib/formatters";
import { queryKeys } from "@/lib/query-keys";
import type { ContentEntry, ContentExportFormat } from "@/types/content";

const CONTENT_TYPE = "pages";
const PAGE_SIZE = 50;

const REVIEW_STATUS_OPTIONS = [
  "DRAFT",
  "SUBMITTED",
  "APPROVED",
  "REJECTED",
] as const;

const LOCALE_OPTIONS = [
  { value: "ar", label: "العربية" },
  { value: "en", label: "English" },
] as const;

const LOCALE_LABEL: Record<string, string> = {
  ar: "العربية",
  en: "English",
};

type StatusFilter = "all" | "published" | "draft";

export default function ContentPage() {
  const { canReview, canPublish, canCreatePage } = useContentPermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusParam = searchParams.get("status");
  const status: StatusFilter =
    statusParam === "published" || statusParam === "draft"
      ? statusParam
      : "all";
  const reviewStatus = searchParams.get("reviewStatus") ?? "all";
  const locale = searchParams.get("locale") ?? "all";
  const department = searchParams.get("department") ?? "all";
  const isNews = searchParams.get("isNews") ?? "all";
  const authorId = searchParams.get("author") ?? "all";
  const search = searchParams.get("search") ?? "";
  const from = searchParams.get("from") ?? "";
  const to = searchParams.get("to") ?? "";

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState(search);
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [targetDept, setTargetDept] = useState<string>("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);

  const patchParams = (updates: Record<string, string | undefined>) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        next.delete("view");
        next.delete("tab");
        return next;
      },
      { replace: true },
    );
  };

  const setFilter = (key: string, value: string) => {
    patchParams({ [key]: value === "all" ? undefined : value });
    setPage(1);
    setSelected(new Set());
  };

  const applySearch = () => {
    patchParams({ search: searchInput.trim() || undefined });
    setPage(1);
    setSelected(new Set());
  };

  const { data: deptData } = useDepartments({ limit: 100 });
  const allDepartments = useMemo(() => deptData?.items ?? [], [deptData]);
  const departments = useAllowedDepartments(allDepartments);
  const deptName = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of departments) m.set(d.id, d.nameAr ?? d.name);
    return m;
  }, [departments]);

  const { data: usersData } = useQuery({
    queryKey: queryKeys.users.list({ pageSize: 100 }),
    queryFn: () => userService.list({ limit: 100 }),
    enabled: canReview,
  });
  const users = usersData?.items ?? [];

  const bulkAssign = useAssignDepartmentBulk();
  const bulkPublish = usePublishBulk();

  const listQuery: ContentListParams = {
    contentType: CONTENT_TYPE,
    pageSize: PAGE_SIZE,
    unassigned: unassignedOnly || undefined,
    status: status === "all" ? undefined : status,
    reviewStatus: reviewStatus === "all" ? undefined : reviewStatus,
    locale: locale === "all" ? undefined : locale,
    departmentId: department === "all" ? undefined : department,
    isNews: isNews === "all" ? undefined : isNews,
    authorId: authorId === "all" ? undefined : authorId,
    search: search || undefined,
    from: from || undefined,
    to: to || undefined,
  };

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.content.list({ ...listQuery, page }),
    queryFn: () => contentService.list({ ...listQuery, page }),
  });

  const exportFile = async (format: ContentExportFormat) => {
    setIsExporting(true);
    try {
      await contentService.exportFile(
        {
          ...listQuery,
          ...(selected.size > 0 && { ids: [...selected].join(",") }),
        },
        format,
      );
    } catch (err) {
      handleApiError(err, { customMessage: "تعذّر تصدير المحتوى" });
    } finally {
      setIsExporting(false);
    }
  };

  const items = data?.items ?? [];
  const pageCount = data?.pagination.pageCount ?? 1;
  const total = data?.pagination.total ?? 0;

  const idOf = (e: ContentEntry) => String(e.id);
  const allOnPageSelected =
    items.length > 0 && items.every((e) => selected.has(idOf(e)));

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) items.forEach((e) => next.delete(idOf(e)));
      else items.forEach((e) => next.add(idOf(e)));
      return next;
    });

  const assign = async () => {
    if (selected.size === 0) return;
    await bulkAssign.mutateAsync({
      contentType: CONTENT_TYPE,
      ids: [...selected],
      departmentId: targetDept || null,
    });
    setSelected(new Set());
  };

  const publishSelected = async () => {
    if (selected.size === 0) return;
    await bulkPublish.mutateAsync({
      contentType: CONTENT_TYPE,
      ids: [...selected],
    });
    setSelected(new Set());
  };

  const showBulkBar = canReview || canPublish;

  return (
    <AdminPageShell
      title="الصفحات"
      description="تصنيف الصفحات إلى الدوائر والموافقة على المحتوى"
      isLoading={isLoading}
      error={error as Error | null}
    >
          <div className="mb-3 flex flex-wrap items-end gap-3">
            {canCreatePage && <CreatePageDialog />}

            <div className="flex min-w-[200px] flex-1 items-center gap-2">
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applySearch()}
                placeholder="بحث بالعنوان..."
                className="h-9"
              />
              <Button variant="outline" size="icon" className="size-9" onClick={applySearch}>
                <Search className="size-4" />
              </Button>
            </div>

            <Select value={status} onValueChange={(v) => setFilter("status", v)}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="حالة النشر" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">كل حالات النشر</SelectItem>
                <SelectItem value="published">المنشور</SelectItem>
                <SelectItem value="draft">المسودات</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={reviewStatus}
              onValueChange={(v) => setFilter("reviewStatus", v)}
            >
              <SelectTrigger className="w-36">
                <SelectValue placeholder="حالة المراجعة" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">كل حالات المراجعة</SelectItem>
                {REVIEW_STATUS_OPTIONS.map((rs) => (
                  <SelectItem key={rs} value={rs}>
                    {REVIEW_STATUS_LABEL[rs]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={department}
              onValueChange={(v) => setFilter("department", v)}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="الدائرة" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">كل الدوائر</SelectItem>
                {departments.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.nameAr ?? d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={locale} onValueChange={(v) => setFilter("locale", v)}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="اللغة" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">كل اللغات</SelectItem>
                {LOCALE_OPTIONS.map((l) => (
                  <SelectItem key={l.value} value={l.value}>
                    {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={isNews} onValueChange={(v) => setFilter("isNews", v)}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="النوع" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">الكل</SelectItem>
                <SelectItem value="false">صفحات</SelectItem>
                <SelectItem value="true">أخبار</SelectItem>
              </SelectContent>
            </Select>

            {canReview && (
              <Select value={authorId} onValueChange={(v) => setFilter("author", v)}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="أُنشئ بواسطة" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع المنشئين</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {[u.firstName, u.lastName].filter(Boolean).join(" ") || u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <div className="flex shrink-0 items-end gap-3">
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                من
                <input
                  type="date"
                  value={from}
                  onChange={(e) => {
                    patchParams({ from: e.target.value || undefined });
                    setPage(1);
                  }}
                  className="h-9 rounded-md border border-border bg-transparent px-2 text-sm"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                إلى
                <input
                  type="date"
                  value={to}
                  onChange={(e) => {
                    patchParams({ to: e.target.value || undefined });
                    setPage(1);
                  }}
                  className="h-9 rounded-md border border-border bg-transparent px-2 text-sm"
                />
              </label>

              <ExportFormatMenu
                isExporting={isExporting}
                onExport={exportFile}
                label={selected.size > 0 ? `تصدير (${selected.size})` : "تصدير"}
              />
            </div>
          </div>

          {showBulkBar && (
            <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg bg-muted/30 p-3">
              {canReview && (
                <>
                  <span className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={unassignedOnly}
                      onCheckedChange={(v) => {
                        setUnassignedOnly(!!v);
                        setPage(1);
                        setSelected(new Set());
                      }}
                    />
                    غير مصنّفة فقط
                  </span>

                  <div className="mx-2 h-5 w-px bg-border" />

                  <Select value={targetDept} onValueChange={setTargetDept}>
                    <SelectTrigger className="w-52">
                      <SelectValue placeholder="اختر الدائرة للتعيين" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.nameAr ?? d.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Button
                    size="sm"
                    disabled={
                      selected.size === 0 || !targetDept || bulkAssign.isPending
                    }
                    onClick={assign}
                  >
                    تعيين المحدد ({selected.size})
                  </Button>
                </>
              )}

              {canPublish && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={selected.size === 0 || bulkPublish.isPending}
                  onClick={publishSelected}
                >
                  <Upload className="ml-1 size-3.5" />
                  نشر المحدد ({selected.size})
                </Button>
              )}

              <span className="ml-auto text-xs text-muted-foreground">
                {total} صفحة {unassignedOnly ? "غير مصنّفة" : "إجمالاً"}
              </span>
            </div>
          )}

          <div className="overflow-hidden rounded-xl border border-border bg-card text-sm shadow-sm">
            <table className="min-w-full divide-y divide-border text-xs">
              <thead className="bg-muted/30 text-[11px] text-muted-foreground">
                <tr>
                  <th className="w-8 px-2 py-2">
                    <Checkbox
                      checked={allOnPageSelected}
                      onCheckedChange={toggleAll}
                    />
                  </th>
                  <th className="px-2 py-2 text-right">العنوان</th>
                  <th className="px-2 py-2 text-center">النوع</th>
                  <th className="px-2 py-2 text-center">اللغة</th>
                  <th className="px-2 py-2 text-center">المراجعة</th>
                  <th className="px-2 py-2 text-center">النشر</th>
                  <th className="px-2 py-2 text-center">الدائرة</th>
                  <th className="px-2 py-2 text-center">أُنشئ بواسطة</th>
                  <th className="px-2 py-2 text-center">آخر تحديث</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((e) => {
                  const id = idOf(e);
                  const dept =
                    typeof e.departmentId === "string"
                      ? (deptName.get(e.departmentId) ?? "—")
                      : "—";
                  return (
                    <tr key={id} className="hover:bg-muted/20">
                      <td className="px-2 py-1.5 text-center">
                        <Checkbox
                          checked={selected.has(id)}
                          onCheckedChange={() => toggle(id)}
                        />
                      </td>
                      <td className="px-2 py-1.5 text-right" dir="rtl">
                        <span className="min-w-0">{e.title ?? "—"}</span>
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <ContentTypeBadge isNews={e.isNews} />
                      </td>
                      <td className="px-2 py-1.5 text-center text-muted-foreground">
                        {typeof e.locale === "string"
                          ? (LOCALE_LABEL[e.locale] ?? e.locale)
                          : "—"}
                      </td>
                      <td className="px-2 py-1.5 text-center align-middle">
                        <ContentReviewBadge entry={e} />
                      </td>
                      <td className="px-2 py-1.5 text-center align-middle">
                        <ContentPublishBadge entry={e} />
                      </td>
                      <td className="px-2 py-1.5 text-center">{dept}</td>
                      <td className="px-2 py-1.5 text-center text-muted-foreground">
                        {e.authorName ?? "—"}
                      </td>
                      <td className="px-2 py-1.5 text-center text-muted-foreground">
                        {formatOptionalArabicDateTime(e.updatedAt)}
                      </td>
                      <td className="px-2 py-1.5">
                        <ContentPageActions
                          entry={e}
                          departmentName={dept === "—" ? undefined : dept}
                        />
                      </td>
                    </tr>
                  );
                })}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={10} className="p-0">
                      <EmptyState icon={FileText} title="لا توجد صفحات" />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="flex items-center justify-between border-t border-border bg-muted/20 px-3 py-1.5 text-[11px]">
              <span className="text-muted-foreground">
                صفحة {page} من {pageCount}
              </span>
              <div className="flex gap-1.5">
                <Button
                  variant="outline"
                  size="icon"
                  className="size-7"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-label="الصفحة السابقة"
                >
                  <ChevronRight className="size-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="size-7"
                  disabled={page >= pageCount}
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  aria-label="الصفحة التالية"
                >
                  <ChevronLeft className="size-4" />
                </Button>
              </div>
            </div>
          </div>
    </AdminPageShell>
  );
}
